import React from 'react';
import { ArrowDown, ArrowUp, Check, ChevronsUpDown, Download, FileText, Pencil, Trash2, X } from 'lucide-react';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import { AppSelect } from '../../../components/AppSelect';

export type TemplateItem = {
  id: string;
  versionId?: string;
  name?: string;
  category?: string;
  createdAt?: number;
  size?: number;
  type?: string;
  hasSignFields?: boolean;
};

type TemplatesListProps = {
  templates: TemplateItem[];
  folders: string[];
  loading: boolean;
  error: string | null;
  onSelect: (template: TemplateItem) => void;
  editingTemplateId: string | null;
  editName: string;
  editCategory: string;
  setEditName: (value: string) => void;
  setEditCategory: (value: string) => void;
  onStartEdit: (template: TemplateItem) => void;
  onSaveEdit: (template: TemplateItem) => void;
  onCancelEdit: () => void;
  savingEditTemplateId?: string | null;
  onDelete: (template: TemplateItem) => void;
  deletingTemplateId?: string | null;
  onDownload: (template: TemplateItem) => void;
  downloadingTemplateId?: string | null;
};

type SortKey = 'name' | 'category' | 'size' | 'createdAt';
type SortDir = 'asc' | 'desc';

const TH_ACTIONS_CLASS =
  'sticky top-0 z-10 w-24 border-b border-app-border bg-app-surface-muted px-3 py-2.5 text-right text-xs font-medium text-app-text-muted';

function compareText(a: string, b: string): number {
  return a.localeCompare(b, 'de', { sensitivity: 'base', numeric: true });
}

function formatDate(ts?: number): string {
  if (!ts || !Number.isFinite(ts)) return '-';
  return new Date(ts * 1000).toLocaleString('de-DE');
}

function formatSize(size?: number): string {
  if (!size || !Number.isFinite(size)) return '-';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function SortableHeaderButton({
  label,
  active,
  dir,
  onSort,
}: {
  label: string;
  active: boolean;
  dir: SortDir;
  onSort: () => void;
}) {
  const Icon = !active ? ChevronsUpDown : dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th
      scope="col"
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className="sticky top-0 z-10 border-b border-app-border bg-app-surface-muted px-3 py-2.5"
    >
      <button
        type="button"
        onClick={onSort}
        className={`group/sort inline-flex w-full items-center gap-1 text-xs font-medium transition-colors ${
          active ? 'text-app-text' : 'text-app-text-subtle hover:text-app-text'
        }`}
      >
        <span className="truncate">{label}</span>
        <Icon
          className={`h-3.5 w-3.5 shrink-0 transition-opacity ${
            active ? 'opacity-100' : 'opacity-0 group-hover/sort:opacity-60'
          }`}
          aria-hidden
        />
      </button>
    </th>
  );
}

export function TemplatesList({
  templates,
  folders,
  loading,
  error,
  onSelect,
  editingTemplateId,
  editName,
  editCategory,
  setEditName,
  setEditCategory,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  savingEditTemplateId = null,
  onDelete,
  deletingTemplateId = null,
  onDownload,
  downloadingTemplateId = null,
}: TemplatesListProps) {
  const [sort, setSort] = React.useState<{ key: SortKey; dir: SortDir }>({
    key: 'name',
    dir: 'asc',
  });

  const folderOptions = React.useMemo(
    () => [{ value: '', label: 'Kein Ordner' }, ...folders.map((folder) => ({ value: folder, label: folder }))],
    [folders],
  );

  const toggleSort = (key: SortKey) => {
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' },
    );
  };

  const sortedTemplates = React.useMemo(() => {
    const rows = [...templates];
    const mul = sort.dir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      let cmp = 0;
      switch (sort.key) {
        case 'name':
          cmp = compareText(a.name || 'Unbenannte Vorlage', b.name || 'Unbenannte Vorlage');
          break;
        case 'category':
          cmp = compareText(a.category || '', b.category || '');
          break;
        case 'size':
          cmp = (a.size ?? 0) - (b.size ?? 0);
          break;
        case 'createdAt':
          cmp = (a.createdAt ?? 0) - (b.createdAt ?? 0);
          break;
      }
      return cmp * mul;
    });
    return rows;
  }, [templates, sort]);

  if (loading) {
    return (
      <div className="rounded-xl border border-app-border bg-app-surface p-8">
        <InlineLoadingState label="Lädt Vorlagen..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {error}
      </div>
    );
  }

  if (!templates.length) {
    return (
      <div className="rounded-xl border border-app-border bg-app-surface p-8 text-center text-app-text-subtle text-sm">
        Noch keine Vorlagen vorhanden.
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-app-border bg-app-surface overflow-visible">
      {/* Mobile card list */}
      <div className="md:hidden divide-y divide-app-border">
        {sortedTemplates.map((template) => {
          const isEditing = editingTemplateId === template.id;
          const isSaving = savingEditTemplateId === template.id;
          return (
            <div key={`m_${template.id}_${template.versionId || ''}`}>
              <div
                className="px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-app-hover"
                onClick={() => { if (!isEditing) onSelect(template); }}
              >
                <div className="w-8 h-8 rounded-md bg-blue-50 text-blue-700 flex items-center justify-center shrink-0">
                  <FileText className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-app-text truncate flex items-center gap-1.5">
                    <span className="truncate">{template.name || 'Unbenannte Vorlage'}</span>
                    {template.hasSignFields ? (
                      <span
                        className="shrink-0 inline-flex rounded px-1.5 py-0.5 text-xs font-medium bg-violet-50 text-violet-700"
                        title="Enthält Signaturfeld - siehe Anleitung"
                      >
                        E-Sign
                      </span>
                    ) : null}
                  </p>
                  <p className="text-xs text-app-text-subtle truncate">
                    {template.category || 'Kein Ordner'} · {formatSize(template.size)}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                  {isEditing ? (
                    <>
                      <button
                        type="button"
                        onClick={() => onSaveEdit(template)}
                        disabled={isSaving}
                        className="p-1.5 rounded hover:bg-green-50 disabled:opacity-50"
                        aria-label="Speichern"
                      >
                        <Check className="w-4 h-4 text-green-600" />
                      </button>
                      <button
                        type="button"
                        onClick={onCancelEdit}
                        disabled={isSaving}
                        className="p-1.5 rounded hover:bg-app-hover"
                        aria-label="Abbrechen"
                      >
                        <X className="w-4 h-4 text-app-text-subtle" />
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onStartEdit(template)}
                      className="p-1.5 rounded hover:bg-app-hover"
                      aria-label="Bearbeiten"
                    >
                      <Pencil className="w-4 h-4 text-app-text-subtle" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onDownload(template); }}
                    disabled={downloadingTemplateId === template.id}
                    className="p-1.5 rounded hover:bg-sky-50 disabled:opacity-50"
                    aria-label="Herunterladen"
                    title="Herunterladen"
                  >
                    <Download className="w-4 h-4 text-blue-600" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onDelete(template); }}
                    disabled={deletingTemplateId === template.id}
                    className="p-1.5 rounded hover:bg-red-50 disabled:opacity-50"
                    aria-label="Löschen"
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                </div>
              </div>
              {/* Edit fields — expanded below row when in edit mode */}
              {isEditing && (
                <div className="px-4 pb-3 space-y-2 bg-app-surface-muted border-t border-app-border" onClick={(e) => e.stopPropagation()}>
                  <input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="w-full border border-app-border rounded-lg px-3 py-2 text-sm text-app-text focus:ring-2 focus:ring-pipedrive-blue/20 outline-none"
                    placeholder="Dokumentname"
                  />
                  <AppSelect
                    value={editCategory}
                    onChange={(v) => setEditCategory(v)}
                    options={folderOptions}
                    size="sm"
                    className="w-full"
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Desktop table */}
      <table className="hidden md:table min-w-full text-sm">
        <thead>
          <tr>
            <SortableHeaderButton
              label="Dokument"
              active={sort.key === 'name'}
              dir={sort.dir}
              onSort={() => toggleSort('name')}
            />
            <SortableHeaderButton
              label="Ordner"
              active={sort.key === 'category'}
              dir={sort.dir}
              onSort={() => toggleSort('category')}
            />
            <SortableHeaderButton
              label="Größe"
              active={sort.key === 'size'}
              dir={sort.dir}
              onSort={() => toggleSort('size')}
            />
            <SortableHeaderButton
              label="Erstellt"
              active={sort.key === 'createdAt'}
              dir={sort.dir}
              onSort={() => toggleSort('createdAt')}
            />
            <th scope="col" className={TH_ACTIONS_CLASS} />
          </tr>
        </thead>
        <tbody>
          {sortedTemplates.map((template) => (
            <tr
              key={`${template.id}_${template.versionId || ''}`}
              className="border-b border-app-border hover:bg-app-hover cursor-pointer"
              onClick={() => {
                if (editingTemplateId === template.id) return;
                onSelect(template);
              }}
            >
              <td className="px-3 py-2">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-7 h-7 rounded-md bg-blue-50 text-blue-700 flex items-center justify-center shrink-0">
                    <FileText className="w-3.5 h-3.5" />
                  </div>
                  <div className="min-w-0">
                    {editingTemplateId === template.id ? (
                      <input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full min-w-[220px] border border-app-border rounded px-2 py-1 text-sm text-app-text"
                        placeholder="Dokumentname"
                      />
                    ) : (
                      <p className="font-medium text-app-text truncate flex items-center gap-1.5">
                        <span className="truncate">{template.name || 'Unbenannte Vorlage'}</span>
                        {template.hasSignFields ? (
                          <span
                            className="shrink-0 inline-flex rounded px-1.5 py-0.5 text-xs font-medium bg-violet-50 text-violet-700"
                            title="Enthält Signaturfeld - siehe Anleitung"
                          >
                            E-Sign
                          </span>
                        ) : null}
                      </p>
                    )}
                  </div>
                </div>
              </td>
              <td className="px-3 py-2 text-app-text-muted">
                {editingTemplateId === template.id ? (
                  <div className="min-w-[180px]" onClick={(e) => e.stopPropagation()}>
                    <AppSelect
                      value={editCategory}
                      onChange={(value) => setEditCategory(value)}
                      options={folderOptions}
                      size="sm"
                    />
                  </div>
                ) : (
                  template.category || '-'
                )}
              </td>
              <td className="px-3 py-2 text-app-text-muted">{formatSize(template.size)}</td>
              <td className="px-3 py-2 text-app-text-muted">{formatDate(template.createdAt)}</td>
              <td className="px-3 py-2">
                <div className="flex items-center justify-end gap-1">
                  {editingTemplateId === template.id ? (
                    <>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSaveEdit(template);
                        }}
                        className="p-1 rounded hover:bg-green-50 disabled:opacity-50"
                        title="Speichern"
                        aria-label="Speichern"
                        disabled={savingEditTemplateId === template.id}
                      >
                        <Check className="w-4 h-4 text-green-600" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onCancelEdit();
                        }}
                        className="p-1 rounded hover:bg-app-hover"
                        title="Abbrechen"
                        aria-label="Abbrechen"
                        disabled={savingEditTemplateId === template.id}
                      >
                        <X className="w-4 h-4 text-app-text-subtle" />
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onStartEdit(template);
                      }}
                      className="p-1 rounded hover:bg-app-hover"
                      title="Dokument bearbeiten"
                      aria-label="Dokument bearbeiten"
                    >
                      <Pencil className="w-4 h-4 text-app-text-subtle" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDownload(template);
                    }}
                    className="p-1 rounded hover:bg-sky-50 disabled:opacity-50"
                    title="Dokument herunterladen"
                    aria-label="Dokument herunterladen"
                    disabled={downloadingTemplateId === template.id}
                  >
                    <Download className="w-4 h-4 text-blue-600" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(template);
                    }}
                    className="p-1 rounded hover:bg-red-50 disabled:opacity-50"
                    title="Dokument löschen"
                    aria-label="Dokument löschen"
                    disabled={deletingTemplateId === template.id}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
